import React from "react";
import { useNewsStore } from "../../../../../../stores/use-news-store";
import {
  NewsContainer,
  NewsWrapper,
  NewsTitle,
  NewsOverviewContainer,
  NewsOverviewMagazineContainer,
  NewsOverviewShortListContainer,
} from "./styles";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import { NewsCard, NewsList, NewsShortList } from "@caelor/ui";
import {
  DEFAULT_BORDER_RADIUS,
  DEFAULT_LABELS_COLOR,
  DEFAULT_SPACES_COLOR,
} from "../../../../../../features/news/defaults";
import { MagazinePreview } from "./MagazinePreview";
import { MACRO_KEY } from "../../../../../defaults";
import { useStyleguideColors } from "../../../../../../hooks/use-styleguide-colors";
import { buildTemplate, NEWS_TEMPLATES } from "./news-templates";
import { INTERIOR_URL, WOMAN_BIKE_URL, WOMAN_URL } from "./images";

const MagazineRender = ({ color, borderRadius }) => (
  <div style={{ width: 450 }}>
    <MagazinePreview color={color} borderRadius={borderRadius} />
  </div>
);

const ListRender = ({ color, borderRadius }) => (
  <div style={{ display: "flex", flexFlow: "column", gap: 10 }}>
    <div style={{ width: 350 }}>
      <NewsList
        title="Customer Spotlight: How MoveFast Uses Velterra for Urban Delivery"
        authorName="Angelica Lorens"
        authorUrl="/img/news/templates/angelicaNews.svg"
        description="A success story about a delivery service scaling with Velterra bikes."
        coverPictureCallback={async () => WOMAN_BIKE_URL}
        createdDate="Jun 22, 2025"
        space="Inside Velterra"
        labels={["Customers"]}
        spaceColor={color || DEFAULT_SPACES_COLOR}
        labelsColor={color || DEFAULT_LABELS_COLOR}
        borderRadius={borderRadius}
        isTemplate
      />
    </div>
    <div style={{ width: 350 }}>
      <NewsList
        title="Amsterdam HQ Gets a New Employee Lounge"
        authorName="Liam Brooks"
        authorUrl="/img/news/templates/liamNews.svg"
        description="Announcing the new employee space with reactions and photos."
        coverPictureCallback={async () => INTERIOR_URL}
        createdDate="Jun 16, 2025"
        space="Marketing"
        labels={["Product", "Design"]}
        spaceColor={color || DEFAULT_SPACES_COLOR}
        labelsColor={color || DEFAULT_LABELS_COLOR}
        borderRadius={borderRadius}
        isTemplate
      />
    </div>
  </div>
);

const ShortListRender = ({ color, borderRadius }) => (
  <div style={{ display: "flex", flexFlow: "column", gap: 5 }}>
    <div style={{ width: 450 }}>
      <NewsShortList
        title="Customer Spotlight: How MoveFast Uses Velterra for Urban Delivery"
        authorName="Liam Brooks"
        authorUrl="/img/news/templates/liamNews.svg"
        description="Get more done, faster: Performance upgrades across the platform"
        coverPictureCallback={async () => WOMAN_BIKE_URL}
        createdDate="Jun 16, 2025"
        space="Inside Velterra"
        labels={["Customers", "Product"]}
        spaceColor={color || DEFAULT_SPACES_COLOR}
        labelsColor={color || DEFAULT_LABELS_COLOR}
        borderRadius={borderRadius}
        isTemplate
      />
    </div>
    <div style={{ width: 450 }}>
      <NewsShortList
        title="Amsterdam HQ Gets a New Employee Lounge"
        authorName="Angelica Lorens"
        authorUrl="/img/news/templates/angelicaNews.svg"
        description="Get more done, faster: Performance upgrades across the platform"
        coverPictureCallback={async () => INTERIOR_URL}
        createdDate="Jun 16, 2025"
        space="Inside Velterra"
        labels={["Workplace"]}
        spaceColor={color || DEFAULT_SPACES_COLOR}
        labelsColor={color || DEFAULT_LABELS_COLOR}
        borderRadius={borderRadius}
        isTemplate
      />
    </div>
    <div style={{ width: 450 }}>
      <NewsShortList
        title="Announcing security review updates for Atlassian marketplace partners"
        authorName="Adam Smith"
        authorUrl="/img/news/templates/adamNews.svg"
        description="Get more done, faster: Performance upgrades across the platform"
        coverPictureCallback={async () => WOMAN_URL}
        createdDate="Jun 16, 2025"
        space="Marketing"
        labels={["Product", "Design"]}
        spaceColor={color || DEFAULT_SPACES_COLOR}
        labelsColor={color || DEFAULT_LABELS_COLOR}
        borderRadius={borderRadius}
        isTemplate
      />
    </div>
  </div>
);

const CardRender = ({ color, borderRadius }) => (
  <div style={{ display: "flex", flexFlow: "row", gap: 8 }}>
    <div style={{ width: 155 }}>
      <NewsCard
        title="Our apps are evolving to meet the needs of modern teams..."
        authorName="Adam Smith"
        authorUrl="/img/news/templates/adamNews.svg"
        description="We’ve made under-the-hood improvements to ensure..."
        coverPictureCallback={async () => WOMAN_BIKE_URL}
        createdDate="Jun 22, 2025"
        space="Marketing"
        labels={["Customers", "Product"]}
        spaceColor={color || DEFAULT_SPACES_COLOR}
        labelsColor={color || DEFAULT_LABELS_COLOR}
        borderRadius={borderRadius}
        isTemplate
      />
    </div>
    <div style={{ width: 155 }}>
      <NewsCard
        title="Amsterdam HQ Gets a New Employee Lounge"
        authorName="Angelica Lorens"
        authorUrl="/img/news/templates/angelicaNews.svg"
        description="Because collaboration should be seamless, not..."
        coverPictureCallback={async () => INTERIOR_URL}
        createdDate="Jun 22, 2025"
        space="Marketing"
        labels={["Product", "Design"]}
        spaceColor={color || DEFAULT_SPACES_COLOR}
        labelsColor={color || DEFAULT_LABELS_COLOR}
        borderRadius={borderRadius}
        isTemplate
      />
    </div>
  </div>
);

const VIEWS = {
  magazine: { label: "Magazine", View: MagazineRender },
  list: { label: "List", View: ListRender },
  short_list: { label: "Short List", View: ShortListRender },
  card: { label: "Card", View: CardRender },
};

export const OverviewTemplates = () => {
  const { setData } = useNewsStore((state) => ({
    setData: state.setData,
  }));
  const { setMacro, setDrawers, colorPalette, styleguideSetup } =
    useOpticsStore((state) => ({
      setMacro: state.setMacro,
      setDrawers: state.setDrawers,
      colorPalette: state.colorPalette,
      styleguideSetup: state.styleguideSetup,
    }));

  const borderRadius = styleguideSetup?.cornerRadius || DEFAULT_BORDER_RADIUS;

  return (
    <NewsOverviewContainer>
      <NewsOverviewMagazineContainer
        onClick={() => {
          setMacro({ key: MACRO_KEY.NEWS });
          setData({ ...buildTemplate("magazine", borderRadius) });
          setDrawers({ templates: false, settings: true });
        }}
      >
        <NewsCard
          title="Customer Spotlight: How MoveFast Uses Velterra for Urban Delivery"
          authorName="Adam Smith"
          authorUrl="/img/news/templates/adamNews.svg"
          description="A success story about a delivery service scaling with Velterra bikes."
          coverPictureCallback={() =>
            Promise.resolve("/img/news/templates/newsCard.svg")
          }
          createdDate="Jun 22, 2025"
          space="Inside Velterra"
          labels={["Customers", "Product"]}
          labelsColor={colorPalette || DEFAULT_LABELS_COLOR}
          spaceColor={colorPalette || DEFAULT_SPACES_COLOR}
          borderRadius={borderRadius}
        />
        <NewsOverviewShortListContainer flex="0 0 26%" coverSize="105px">
          <NewsShortList
            title="Amsterdam HQ Gets a New Employee Lounge"
            coverPictureCallback={() =>
              Promise.resolve("/img/news/templates/newsShortList.svg")
            }
            createdDate="Dec 22, 2024"
            space="Inside Velterra"
            labels={["Customers", "Marketplace"]}
            labelsColor={colorPalette || DEFAULT_LABELS_COLOR}
            spaceColor={colorPalette || DEFAULT_SPACES_COLOR}
            borderRadius={borderRadius}
          />
          <NewsShortList
            title="Meet Laila: From Intern to Product Manager in One Year"
            coverPictureCallback={() =>
              Promise.resolve("/img/news/templates/newsShortList.svg")
            }
            createdDate="Dec 21, 2024"
            space="Team Spotlight"
            labels={["People", "New Starters"]}
            labelsColor={colorPalette || DEFAULT_LABELS_COLOR}
            spaceColor={colorPalette || DEFAULT_SPACES_COLOR}
            borderRadius={borderRadius}
          />
          <NewsShortList
            title="October Bonus Advance for Team Leads"
            coverPictureCallback={() =>
              Promise.resolve("/img/news/templates/newsShortList.svg")
            }
            createdDate="Dec 12, 2024"
            space="Team Spotlight"
            labels={["People"]}
            labelsColor={colorPalette || DEFAULT_LABELS_COLOR}
            spaceColor={colorPalette || DEFAULT_SPACES_COLOR}
            borderRadius={borderRadius}
          />
        </NewsOverviewShortListContainer>
      </NewsOverviewMagazineContainer>

      <NewsOverviewShortListContainer
        hasHover
        flex="0 0 20%"
        coverSize="100px"
        onClick={() => {
          setMacro({ key: MACRO_KEY.NEWS });
          setData({ ...buildTemplate("short_list", borderRadius) });
          setDrawers({ templates: false, settings: true });
        }}
      >
        <NewsShortList
          title="How MoveFast Uses Velterra for Urban Delivery"
          authorName="Amanda Wallace"
          authorUrl="/img/news/templates/angelicaNews.svg"
          description="Get more done, faster: Performance upgrades across the platform"
          coverPictureCallback={() =>
            Promise.resolve("/img/news/templates/newsCard.svg")
          }
          createdDate="Nov 22, 2024"
          space="Inside Velterra"
          labels={["Customers", "Product"]}
          labelsColor={colorPalette || DEFAULT_LABELS_COLOR}
          spaceColor={colorPalette || DEFAULT_SPACES_COLOR}
          borderRadius={borderRadius}
        />
        <NewsShortList
          title="Amsterdam HQ Gets a New Employee Lounge"
          authorName="Adam Smith"
          authorUrl="/img/news/templates/adamNews.svg"
          description="Get more done, faster: Performance upgrades across the platform"
          coverPictureCallback={() =>
            Promise.resolve("/img/news/templates/newsList.svg")
          }
          createdDate="Oct 23, 2024"
          space="Inside Velterra"
          labels={["Customers", "Product"]}
          labelsColor={colorPalette || DEFAULT_LABELS_COLOR}
          spaceColor={colorPalette || DEFAULT_SPACES_COLOR}
          borderRadius={borderRadius}
        />
      </NewsOverviewShortListContainer>
    </NewsOverviewContainer>
  );
};

const NewsTemplates = () => {
  const { data, config, setData } = useNewsStore((state) => ({
    data: state.data,
    config: state.data.config,
    setData: state.setData,
  }));
  const { setDrawers, styleguideSetup } = useOpticsStore((state) => ({
    setDrawers: state.setDrawers,
    styleguideSetup: state.styleguideSetup,
  }));

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });
  const colors = config?.colors;

  const color = colors || styleguideColors;

  const borderRadius =
    config?.cardBorderRadius ||
    styleguideSetup?.cornerRadius ||
    DEFAULT_BORDER_RADIUS;

  const buildData = (template, value) => {
    if (Object.keys(data || {}).length) {
      return {
        ...data,
        config: {
          ...data.config,
          variant: value.variant,
        },
      };
    }

    const newData = {
      ...template,
      config: { ...template.config, ...value },
    };
    return newData;
  };

  return (
    <NewsContainer>
      {NEWS_TEMPLATES.map((template) => {
        const { label, View } = VIEWS[template.config.variant];

        return (
          <NewsWrapper
            key={template.id}
            onClick={() => {
              setData({
                ...buildData(template, {
                  colors: color,
                  cardBorderRadius: borderRadius,
                  variant: template.config.variant,
                }),
              });
              setDrawers({ templates: false, settings: true });
            }}
          >
            <NewsTitle>{label}</NewsTitle>
            <View color={color} borderRadius={borderRadius} />
          </NewsWrapper>
        );
      })}
    </NewsContainer>
  );
};

export default NewsTemplates;
